import React, {
  useState,
  useRef,
  useMemo,
  useCallback,
  useEffect,
} from "react";
import axios from "axios";
import { Trash2, Edit, Plus, Printer, X ,Check, Loader2, Lightbulb } from "lucide-react";
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const Tooltip = ({ content, children }) => {
  const [show, setShow] = useState(false);
  const tooltipRef = useRef(null);

  return (
    <div className="relative inline-block">
      <div
        onMouseEnter={() => setShow(true)}
        onMouseLeave={() => setShow(false)}
      >
        {children}
      </div>
      {show && (
        <div
          ref={tooltipRef}
          className="absolute z-50 p-2 bg-gray-800 text-white text-sm rounded shadow-lg max-w-xs whitespace-normal -top-2 left-full ml-2"
        >
          {content}
        </div>
      )}
    </div>
  );
};

const SearchableSelect = ({
  options,
  value,
  onChange,
  placeholder,
  label,
  allItems = [],
  onSelectItem,
  type = "default",
  disabled = false,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  // Update searchTerm when value changes
  useEffect(() => {
    if (!value) {
      setSearchTerm("");
      return;
    }

    if (type === "itemSearch") {
      const selectedItem = allItems.find((item) => item.ItemID === value);
      if (selectedItem) {
        setSearchTerm(selectedItem.ItemName);
      }
    } else {
      const selectedOption = options.find((opt) => opt.value === value);
      if (selectedOption) {
        setSearchTerm(selectedOption.label);
      }
    }
  }, [value, options, allItems, type]);

  const filteredOptions = useMemo(() => {
    const searchLower = searchTerm.toLowerCase();
    if (type === "itemSearch") {
      return allItems.filter(
        (item) =>
          item && // Add null check here
          (item.ItemName.toLowerCase().includes(searchLower) ||
            item.ItemID.toString().toLowerCase().includes(searchLower))
      );
    }
    return options.filter(
      (opt) =>
        opt && // Add null check here
        (opt.label.toLowerCase().includes(searchLower) ||
          opt.value.toString().includes(searchLower))
    );
  }, [options, searchTerm, type, allItems]);

  const handleSelectOption = useCallback(
    (option) => {
      if (type === "itemSearch") {
        onSelectItem && onSelectItem(option);
        setSearchTerm(option ? option.ItemName : "");
      } else {
        onChange(option ? option.value : "");
        setSearchTerm(option ? option.label : "");
      }
      setIsOpen(false);
      setSelectedIndex(-1);
    },
    [onChange, onSelectItem, type]
  );

  const handleKeyDown = useCallback(
    (e) => {
      if (!isOpen) {
        if (e.key === "ArrowDown" || e.key === "ArrowUp") {
          setIsOpen(true);
          return;
        }
      }

      if (filteredOptions.length === 0) return;

      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          setSelectedIndex((prev) => {
            const newIndex = prev < filteredOptions.length - 1 ? prev + 1 : 0;
            if (listRef.current?.children[newIndex]) {
              listRef.current.children[newIndex].scrollIntoView({
                block: "nearest",
              });
            }
            return newIndex;
          });
          break;
        case "ArrowUp":
          e.preventDefault();
          setSelectedIndex((prev) => {
            const newIndex = prev > 0 ? prev - 1 : filteredOptions.length - 1;
            if (listRef.current?.children[newIndex]) {
              listRef.current.children[newIndex].scrollIntoView({
                block: "nearest",
              });
            }
            return newIndex;
          });
          break;
        case "Enter":
          e.preventDefault();
          if (selectedIndex >= 0 && selectedIndex < filteredOptions.length) {
            handleSelectOption(filteredOptions[selectedIndex]);
          }
          break;
        case "Escape":
          setIsOpen(false);
          setSelectedIndex(-1);
          break;
        default:
          break;
      }
    },
    [isOpen, filteredOptions, selectedIndex, handleSelectOption]
  );

  const clearSelection = useCallback(
    (e) => {
      e.stopPropagation();
      setSearchTerm("");
      if (type === "itemSearch") {
        onSelectItem && onSelectItem(null);
      } else {
        onChange("");
      }
      setIsOpen(false);
      setSelectedIndex(-1);
      inputRef.current?.focus();
    },
    [onChange, onSelectItem, type]
  );

  const handleInputChange = useCallback(
    (e) => {
      const newValue = e.target.value;
      setSearchTerm(newValue);
      setIsOpen(true);
      setSelectedIndex(-1);

      if (newValue === "") {
        if (type === "itemSearch") {
          onSelectItem && onSelectItem(null);
        } else {
          onChange("");
        }
      }
    },
    [onChange, onSelectItem, type]
  );

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSelectedIndex(-1);
        // Reset to selected value if user clicks outside without selecting
        if (value) {
          if (type === "itemSearch") {
            const selectedItem = allItems.find((item) => item.ItemID === value);
            if (selectedItem) {
              setSearchTerm(selectedItem.ItemName);
            }
          } else {
            const selectedOption = options.find((opt) => opt.value === value);
            if (selectedOption) {
              setSearchTerm(selectedOption.label);
            }
          }
        } else {
          setSearchTerm("");
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [value, options, allItems, type]);

  return (
    <div className="relative mb-4" ref={dropdownRef}>
      <label className="block text-sm font-medium mb-1">{label}</label>
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          className={`w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500 
            ${disabled ? "bg-gray-100" : ""} 
            ${!disabled && isOpen ? "rounded-b-none" : ""}`}
          placeholder={`Search ${placeholder}`}
          value={searchTerm}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => !disabled && setIsOpen(true)}
          disabled={disabled}
        />
        {searchTerm && !disabled && (
          <button
            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            onClick={clearSelection}
            type="button"
          >
            <X size={16} />
          </button>
        )}
      </div>
      {isOpen && filteredOptions.length > 0 && !disabled && (
        <div
          ref={listRef}
          className="absolute z-50 w-full max-h-60 overflow-auto bg-white border-x border-b rounded-b shadow-lg"
        >
          {filteredOptions.map((option, index) => (
            <div
              key={type === "itemSearch" ? option.ItemID : option.value}
              className={`px-3 py-2 cursor-pointer hover:bg-gray-100 ${
                index === selectedIndex ? "bg-blue-50 text-blue-600" : ""
              }`}
              onClick={() => handleSelectOption(option)}
            >
              <div className="flex justify-between items-center">
                <span>
                  {type === "itemSearch" ? option.ItemName : option.label}
                </span>
                <span className="text-gray-400 text-sm">
                  ID: {type === "itemSearch" ? option.ItemID : option.value}
                </span>
              </div>
              {type === "itemSearch" && option.CategoryName && (
                <div className="text-sm text-gray-500">
                  Category: {option.CategoryName}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const MobileOptimizedModal = ({ onClose, children }) => {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="relative bg-white rounded-lg w-full max-w-md max-h-[90vh] overflow-y-auto">
        <button
          className="absolute top-3 right-3 text-gray-600 hover:text-gray-900 z-10"
          onClick={onClose}
        >
          <X size={24} />
        </button>
        {children}
      </div>
    </div>
  );
};

const Modal = ({ onClose, children }) => {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg p-6 w-[800px] max-h-[80vh] overflow-y-auto relative">
        <button
          className="absolute top-3 right-3 text-gray-600 hover:text-gray-900"
          onClick={onClose}
        >
          <X size={24} />
        </button>
        {children}
      </div>
    </div>
  );
};

const PHItemsTable = ({
  indentID,
  onAddItem,
  onDeleteItem,
  calculateTotalAmount,
  onUpdateItem,
  isDraft,
  isEditable = true,
  onSubmit,
  onSaveDraft,
  onPrint,
  indentStatus = { l1a: 0, l1: 0, l2: 0, po: 0, ph: 0 },
}) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [allItems, setAllItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [items, setItems] = useState([]);
  const [error, setError] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [loading, setLoading] = useState(false); // Added loading state
  const [accountingUnits, setAccountingUnits] = useState([]);
  const [editingItem, setEditingItem] = useState(null);
  const [itemsUpdated, setItemsUpdated] = useState(false);
  const [itemDescriptions, setItemDescriptions] = useState({});
  const [loadingDescriptions, setLoadingDescriptions] = useState({});
  const [newItem, setNewItem] = useState({
    categoryID: "",
    itemID: "",
    quantity: 1,
    stock: 0,
    total: 0,
    remarks: "",
    normalDays: 0,
    promisedDate: "",
    commitedDate: "",
    accountingUnitID: "",
    accountingUnitName: "",
  });

  // ... existing state
  const [changeLog, setChangeLog] = useState({
    addedItems: [],
    deletedItems: [],
    updatedItems: [],
  });

  const fetchItemDescription = async (itemId) => {
    if (loadingDescriptions[itemId] || itemDescriptions[itemId]) return;

    console.log("Fetching description for item:", itemId);
    setLoadingDescriptions((prev) => ({ ...prev, [itemId]: true }));

    try {
      const response = await axios.get(
        `${API_BASE_URL}/viewTable/itemdescription/${itemId}`,
        { params: { indentId: indentID } }
      );

      console.log("Description API response:", response.data);

      if (response.data && response.data.Description) {
        setItemDescriptions((prev) => ({
          ...prev,
          [itemId]: response.data.Description,
        }));
      }
    } catch (error) {
      console.error("Error fetching description:", error);
    } finally {
      setLoadingDescriptions((prev) => ({ ...prev, [itemId]: false }));
    }
  };

  // Add useEffect to pre-fetch descriptions for all items
  useEffect(() => {
    if (items.length > 0) {
      items.forEach((item) => {
        if (item.itemID && !itemDescriptions[item.itemID]) {
          fetchItemDescription(item.itemID);
        }
      });
    }
  }, [items]);

  const refreshItemsData = async () => {
    if (!indentID) return;

    setIsRefreshing(true);
    try {
      const response = await axios.get(
        `${API_BASE_URL}/viewTable/indentitems`,
        {
          params: { indentId: indentID },
        }
      );

      const fetchedItems = response.data
        .filter((item) => item.IndentID === indentID)
        .map((item) => ({
          indentItemID: item.IndentItemID,
          itemName: item.ItemName,
          quantity: item.Quantity,
          stock: item.Stock || 0,
          categoryID: item.CategoryID,
          itemID: item.ItemID,
          remarks: item.Description,
          promisedDate: item.PromisedDate,
          normalDays: item.NormalDays,
          accountingUnitID: item.AccountingUnitID,
          unitname: item.UnitName, // Make sure this is included
        }));

      setItems(fetchedItems);
    } catch (error) {
      console.error("Error refreshing items:", error);
      setError("Failed to refresh items. Please try again.");
    } finally {
      setIsRefreshing(false);
    }
  };


  // Memoized function to fetch items
  const fetchItems = async () => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/viewTable/indentitems`,
        {
          params: { indentId: indentID },
        }
      );

      const fetchedItems = response.data
        .filter((item) => item.IndentID === indentID)
        .map((item) => {
          // Format the PromisedDate to dd-mm-yy
          const formattedDate = item.PromisedDate
            ? new Date(item.PromisedDate).toLocaleDateString("en-GB", {
                day: "2-digit",
                month: "2-digit",
                year: "2-digit",
              })
            : null;
          const formattedDate2 = item.CommitedDate
            ? new Date(item.CommitedDate).toLocaleDateString("en-GB", {
                day: "2-digit",
                month: "2-digit",
                year: "2-digit",
              })
            : null;

          return {
            indentItemID: item.IndentItemID,
            itemName: item.ItemName,
            quantity: item.Quantity,
            stock: item.Stock || 0,
            categoryID: item.CategoryID,
            itemID: item.ItemID,
            remarks: item.Description,
            promisedDate: formattedDate, // Use the formatted date
            commitedDate: formattedDate2, // Use the formatted date
            accountingUnitID: item.AccountingUnitID,
            unitname: item.UnitName,
          };
        });

      setItems(fetchedItems);
      return fetchedItems;
    } catch (error) {
      console.error("Error fetching items:", error);
      return [];
    }
  };

  // Fetch categories and items when the component mounts
  useEffect(() => {
    const fetchAllData = async () => {
      try {
        const [categoryResponse, itemResponse, accountingUnitsResponse] =
          await Promise.all([
            axios.get(`${API_BASE_URL}/dropdown/mcatdrop`),
            axios.get(`${API_BASE_URL}/dropdown/mitemdrop`),
            axios.get(`${API_BASE_URL}/dropdown/maudrop`),
          ]);

        setCategories(categoryResponse.data || []);
        setAllItems(itemResponse.data || []);
        setAccountingUnits(accountingUnitsResponse.data || []);

        // Fetch items separately to avoid race conditions
        await fetchItems();
      } catch (error) {
        console.error("Error fetching data:", error);
        setCategories([]);
        setAllItems([]);
        setItems([]);
      }
    };

    if (indentID) {
      fetchAllData();
    }
  }, [indentID]);

  const handleItemChange = (itemId) => {
    const parsedItemId = parseInt(itemId, 10);
    const selectedItem = filteredItems.find(
      (item) => item.ItemID === parsedItemId
    );

    setNewItem((prev) => ({
      ...prev,
      itemID: parsedItemId,
      accountingUnitID: selectedItem?.AccountingUnitID || "",
      accountingUnitName: selectedItem?.UnitName || "",
      stock: selectedItem?.Stock || 0,
    }));
  };

  const handleCategoryChange = (categoryId) => {
    if (!categoryId) {
      setNewItem((prev) => ({
        ...prev,
        categoryID: "",
        itemID: "",
        normalDays: 0,
        commitedDate: "",
        accountingUnitID: "",
        accountingUnitName: "",
      }));
      setFilteredItems([]);
      return;
    }
    const parsedCategoryId = parseInt(categoryId, 10);
    const selectedCategory = categories.find(
      (cat) => cat.CategoryID === parsedCategoryId
    );

    if (selectedCategory) {
      const currentDate = new Date();
      const promisedDate = new Date(currentDate);
      promisedDate.setDate(
        currentDate.getDate() + (selectedCategory?.NormalDays || 0)
      );

      setNewItem((prev) => ({
        ...prev,
        categoryID: parsedCategoryId,
        itemID: "", // Reset itemID
        normalDays: selectedCategory?.NormalDays || 0,
        promisedDate: promisedDate.toISOString().split("T")[0],
        commitedDate: promisedDate.toISOString().split("T")[0],
        accountingUnitID: "", // Reset accountingUnitID
        accountingUnitName: "", // Reset accountingUnitName
      }));

      const filtered = allItems.filter(
        (item) => item.CategoryID === parsedCategoryId
      );
      setFilteredItems(filtered);
    }
  };

  const handleQuantityChange = (value) => {
    // Allow decimal values up to 3 places
    const regex = /^\d*\.?\d{0,3}$/;
    if (regex.test(value)) {
      setNewItem((prev) => ({
        ...prev,
        quantity: value,
      }));
    }
  };

  const handleNewItemFieldChange = (field, value) => {
    if (field === "quantity") {
      // Allow up to 3 decimal places for quantity
      const formattedValue = parseFloat(value).toFixed(3);
      setNewItem((prev) => ({
        ...prev,
        [field]: formattedValue,
      }));
    } else {
      setNewItem((prev) => ({ ...prev, [field]: value }));
    }
  };

  const handleAddNewItem = async () => {
    if (loading) return;
    setLoading(true);

    if (!newItem.quantity || parseFloat(newItem.quantity) <= 0) {
      setError("Please enter a valid quantity");
      return;
    }

    const formattedQuantity = parseFloat(newItem.quantity).toFixed(3);

    try {
      const isDuplicateItem = items.some(
        (existingItem) => existingItem.itemID === newItem.itemID
      );

      if (isDuplicateItem) {
        setError(
          `Item "${
            filteredItems.find((item) => item.ItemID === newItem.itemID)
              ?.ItemName
          }" is already added.`
        );
        return;
      }

      const selectedItem = filteredItems.find(
        (item) => item.ItemID === newItem.itemID
      );

      const accountingUnitID = selectedItem?.AccountingUnitID;
      if (!accountingUnitID) {
        setError(
          "Could not determine the Accounting Unit for the selected item."
        );
        return;
      }

      await axios.post(`${API_BASE_URL}/phpage/indentitems`, {
        IndentID: indentID,
        ItemID: newItem.itemID,
        Quantity: formattedQuantity,
        Description: newItem.remarks || "",
        AccountingUnitID: accountingUnitID,
        CategoryID: newItem.categoryID,
        Stock: newItem.stock,
        PromisedDate: newItem.promisedDate,
        CommitedDate: newItem.commitedDate,
      });

      const updatedItem = {
        ...newItem,
        itemName: selectedItem ? selectedItem.ItemName : "Unnamed Item",
        total: newItem.quantity * newItem.stock,
        accountingUnitID: accountingUnitID,
      };

      // Update local state immediately
      setItems((prevItems) => [...prevItems, updatedItem]);
      setModalOpen(false);
      setError(null);

      // Reset form
      setNewItem({
        categoryID: "",
        itemID: "",
        quantity: 1,
        stock: 0,
        total: 0,
        remarks: "",
        normalDays: 0,
        promisedDate: "",
        commitedDate: "",
        accountingUnitID: "",
        accountingUnitName: "",
      });

      // Fetch updated data in background
      fetchItems();

      onAddItem && onAddItem(updatedItem);
      onUpdateItem &&
        onUpdateItem({
          type: "add",
          item: updatedItem,
          changeLog: {
            ...changeLog,
            addedItems: [...changeLog.addedItems, updatedItem],
          },
        });
    } catch (error) {
      console.error("Error adding item:", error);
      setError("Failed to add item. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteItem = async (index) => {
    const itemToDelete = items[index];
    try {
      await axios.delete(
        `${API_BASE_URL}/phpage/indentitems/${itemToDelete.itemID}`,
        {
          // Use query parameters instead of request body
          params: {
            indentID: indentID,
            itemID: itemToDelete.itemID,
          },
        }
      );

      const updatedItems = items.filter((_, i) => i !== index);
      setItems(updatedItems);

      onDeleteItem && onDeleteItem(index);

      // Track deleted item
      setChangeLog((prev) => ({
        ...prev,
        deletedItems: [...prev.deletedItems, itemToDelete],
      }));

      // Notify parent component about the change
      onUpdateItem &&
        onUpdateItem({
          type: "delete",
          item: itemToDelete,
          changeLog,
        });
    } catch (error) {
      console.error("Error deleting item:", error.response?.data || error);
      alert("Failed to delete item. Please try again.");
    }
  };

  const handleUpdateItem = async () => {
    if (loading) return;
    setLoading(true);

    try {
      const itemToUpdate = editingItem;
      if (!itemToUpdate.itemID || !itemToUpdate.indentItemID) {
        setError("Invalid item ID");
        return;
      }

      const formattedQuantity = parseFloat(editingItem.quantity).toFixed(3);

      await axios.put(
        `${API_BASE_URL}/phpage/indentitems/${itemToUpdate.indentItemID}`,
        {
          Quantity: formattedQuantity,
          Stock: itemToUpdate.stock,
          Description: editingItem.description, // Add description to the update
        }
      );

      setItemDescriptions((prev) => ({
        ...prev,
        [editingItem.itemID]: editingItem.description,
      }));
      await refreshItemsData();
      // Update local state immediately
      setItems((prevItems) =>
        prevItems.map((item) =>
          item.indentItemID === itemToUpdate.indentItemID
            ? { ...item, ...itemToUpdate }
            : item
        )
      );
      setEditingItem(null);
      const updatedItems = items.map((item) => {
        if (item.indentItemID === editingItem.indentItemID) {
          return {
            ...item,
            quantity: formattedQuantity,
            stock: editingItem.stock,
            remarks: editingItem.description, // Update remarks in items array
          };
        }
        return item;
      });

      onUpdateItem({
        type: "update",
        item: editingItem,
        items: updatedItems,
      });
      // Fetch updated data in background
      fetchItems();

      onUpdateItem &&
        onUpdateItem({
          type: "update",
          item: itemToUpdate,
          changeLog: {
            ...changeLog,
            updatedItems: [
              ...changeLog.updatedItems,
              {
                original: items[itemToUpdate.index],
                updated: itemToUpdate,
              },
            ],
          },
        });
    } catch (error) {
      console.error("Error updating item:", error);
      setError("Failed to update item. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (indentID) {
      refreshItemsData();
    }
  }, [indentID]);
  // Refresh items data after updates
  useEffect(() => {
    const refreshItems = async () => {
      if (itemsUpdated && indentID) {
        try {
          console.log("Refreshing items data...");
          const response = await axios.get(
            `${API_BASE_URL}/viewTable/indentitems`,
            {
              params: { indentId: indentID },
            }
          );

          const fetchedItems = response.data
            .filter((item) => item.IndentID === indentID)
            .map((item) => ({
              indentItemID: item.IndentItemID,
              itemName: item.ItemName,
              quantity: item.Quantity,
              stock: item.Stock || 0,
              categoryID: item.CategoryID,
              itemID: item.ItemID,
              remarks: item.Description,
              promisedDate: item.PromisedDate,
              normalDays: item.NormalDays,
              accountingUnitID: item.AccountingUnitID,
              unitname: item.UnitName,
            }));

          console.log("Refreshed items data:", fetchedItems);
          setItems(fetchedItems);
          setItemsUpdated(false);
        } catch (error) {
          console.error("Error refreshing items:", error);
        }
      }
    };

    refreshItems();
  }, [itemsUpdated, indentID]);

  // Recalculate total amount based on local items state
  const calculateTotal = () => {
    return items.reduce((total, item) => total + item.total, 0);
  };

  // Add a method to reset change log when submitting
  const handleFinalSubmit = () => {
    onSubmit();
    // Reset change log after submission
    setChangeLog({
      addedItems: [],
      deletedItems: [],
      updatedItems: [],
    });
  };

  const handleItemSearch = useCallback(
    (selectedItem) => {
      // Handle null/clearing case
      if (!selectedItem) {
        setFilteredItems([]);
        setNewItem((prev) => ({
          ...prev,
          categoryID: "",
          itemID: "",
          accountingUnitID: "",
          accountingUnitName: "",
          stock: 0,
          normalDays: 0,
          promisedDate: "",
          commitedDate: "",
        }));
        return;
      }

      // First set filtered items for the selected category
      const categoryItems = allItems.filter(
        (item) => item.CategoryID === selectedItem.CategoryID
      );
      setFilteredItems(categoryItems);

      // Get the normalDays from the category
      const selectedCategory = categories.find(
        (cat) => cat.CategoryID === selectedItem.CategoryID
      );
      const normalDays = selectedCategory?.NormalDays || 0;

      // Calculate dates
      const currentDate = new Date();
      const promisedDate = new Date(currentDate);
      promisedDate.setDate(currentDate.getDate() + normalDays);

      // Update all state in one go
      setNewItem((prev) => ({
        ...prev,
        categoryID: selectedItem.CategoryID,
        itemID: selectedItem.ItemID,
        accountingUnitID: selectedItem.AccountingUnitID || "",
        accountingUnitName: selectedItem.UnitName || "",
        stock: selectedItem.Stock || 0,
        normalDays: normalDays,
        promisedDate: promisedDate.toISOString().split("T")[0],
        commitedDate: promisedDate.toISOString().split("T")[0],
      }));
    },
    [allItems, categories]
  );

  const isSubmittedStage =
    indentStatus.l1a === 1 &&
    indentStatus.l1 === 1 &&
    indentStatus.l2 === 1 &&
    indentStatus.po === 1 &&
    indentStatus.ph === 1;

    const renderDescriptionCell = (itemId) => {
      if (editingItem?.itemID === itemId) {
        return (
          <div className="flex items-center space-x-2">
            <input
              type="text"
              className="border rounded px-2 py-1 text-sm w-32"
              value={editingItem.description}
              onChange={(e) =>
                setEditingItem((prev) => ({
                  ...prev,
                  description: e.target.value,
                }))
              }
            />
          </div>
        );
      }
    
      if (loadingDescriptions[itemId]) {
        return (
          <div className="w-[18px] h-[18px] mx-auto flex items-center justify-center">
            <Loader2 size={18} className="animate-spin text-blue-500" />
          </div>
        );
      }
    
      const description = itemDescriptions[itemId];
      return (
        <div className="flex items-center space-x-2">
          <Tooltip content={description || "No description available"}>
            <button
              className={`${
                description
                  ? "text-yellow-500 hover:text-yellow-600"
                  : "text-gray-400 hover:text-gray-600"
              } transition-colors duration-200`}
              onClick={() => {
                if (!description) {
                  fetchItemDescription(itemId);
                }
              }}
            >
              <Lightbulb size={18} />
            </button>
          </Tooltip>
        </div>
      );
    };
    
    const handleEditItem = (item, index) => {
      // Ensure we fetch the description if it hasn't been loaded yet
      if (!itemDescriptions[item.itemID]) {
        fetchItemDescription(item.itemID);
      }
      
      setEditingItem({
        ...item,
        index: index,
        indentItemID: item.indentItemID,
        quantity: item.quantity,
        stock: item.stock,
        description: itemDescriptions[item.itemID] || "",
      });
      setItemsUpdated(true);
    };


  return (
    <div className="bg-white shadow-md rounded-lg p-6 border border-gray-200">
      {/* If you want to show a global error */}
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}
      <div className="flex justify-between items-center border-b pb-4 mb-4">
        <h2 className="text-xl font-bold text-gray-800">Invoice Items</h2>

        <div className="flex items-center space-x-2">
          {/* Print Button */}
          <button
            className="text-gray-600 hover:bg-gray-100 p-2 rounded-full transition-colors duration-300"
            onClick={onPrint}
            title="Print Invoice"
          >
            <Printer size={20} />
          </button>

          {!isSubmittedStage && isDraft && isEditable && (
            /* Add Item Button - Circular */
            <button
              className="bg-blue-500 text-white p-2 rounded-full hover:bg-blue-600 transition-colors duration-300"
              onClick={() => setModalOpen(true)}
              title="Add Item"
            >
              <Plus size={20} />
            </button>
          )}
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm text-gray-600">
          <thead className="bg-gray-100 text-gray-700 uppercase">
            <tr>
              <th className="px-4 py-3 text-left w-12">S.No</th>
              <th className="px-4 py-3 text-left">Item Name</th>
              <th className="px-4 py-3 text-center w-8"></th>
              <th className="px-4 py-3 text-left">AU</th>
              <th className="px-4 py-3 text-center w-24">Quantity</th>
              <th className="px-4 py-3 text-right">Stock</th>
              <th className="px-4 py-3 text-right">Commited Date</th>
              {!isSubmittedStage && (
                <th className="px-4 py-3 text-center w-24">Actions</th>
              )}
            </tr>
          </thead>
          <tbody>
            {items.length > 0 ? (
              items.map((item, index) => (
                <tr
                  key={index}
                  className="border-b hover:bg-gray-50 transition-colors duration-200"
                >
                  <td className="px-4 py-3 text-gray-500">{index + 1}</td>
                  <td className="px-4 py-3 font-medium">{item.itemName}</td>
                  <td className="px-4 py-3 text-center">
                    {renderDescriptionCell(item.itemID)}
                  </td>
                  <td className="px-4 py-3 font-medium">{item.unitname}</td>

                  {/* Edit mode for quantity and stock */}
                  {editingItem && editingItem.index === index ? (
                    <>
                      <td className="px-4 py-3">
                        <input
                          type="number"
                          step="0.001"
                          min="0"
                          className="w-full border rounded px-2 py-1"
                          value={editingItem.quantity}
                          onChange={(e) =>
                            setEditingItem((prev) => ({
                              ...prev,
                              quantity: e.target.value,
                            }))
                          }
                        />
                      </td>
                      <td className="px-4 py-3">
                        <input
                          type="number"
                          className="w-full border rounded px-2 py-1"
                          value={editingItem.stock}
                          onChange={(e) =>
                            setEditingItem((prev) => ({
                              ...prev,
                              stock: parseFloat(e.target.value),
                            }))
                          }
                        />
                      </td>
                      <td className="px-4 py-3 text-right">
                        {item.commitedDate}
                      </td>
                      {!isSubmittedStage && (
                        <td className="px-4 py-3 text-center">
                          <div className="flex justify-center space-x-2">
                            <button
                              className="text-green-500 hover:text-green-700"
                              onClick={handleUpdateItem}
                            >
                              Save
                            </button>
                            <button
                              className="text-gray-500 hover:text-gray-700"
                              onClick={() => setEditingItem(null)}
                            >
                              Cancel
                            </button>
                          </div>
                        </td>
                      )}
                    </>
                  ) : (
                    <>
                      <td className="px-4 py-3 text-center">
                        {parseFloat(item.quantity).toFixed(3)}
                      </td>
                      <td className="px-4 py-3 text-right">{item.stock}</td>
                      <td className="px-4 py-3 text-right">
                        {item.commitedDate}
                      </td>
                      {!isSubmittedStage && (
                        <td className="px-4 py-3 text-center">
                          <div className="flex justify-center space-x-2">
                            {isDraft && isEditable && (
                              <>
                                <button
                                  className="text-blue-500 hover:text-blue-700"
                                  onClick={() => handleEditItem(item, index)}
                                >
                                  <Edit size={18} />
                                </button>
                                <button
                                  className="text-red-500 hover:text-red-700"
                                  onClick={() => handleDeleteItem(index)}
                                >
                                  <Trash2 size={18} />
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      )}
                    </>
                  )}
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan={!isSubmittedStage ? 7 : 6}
                  className="text-center text-gray-500 py-6"
                >
                  No items added to the invoice
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-6 pt-4 border-t flex justify-between items-center">
        {/*<div className="text-lg font-bold text-gray-800">
          Total Invoice Amount:
          <span className="text-green-600 ml-2">
            ₹{calculateTotal().toLocaleString()}
          </span>
        </div>*/}
        <div className="flex space-x-4">
          {isSubmittedStage ? (
            <button
              className="bg-green-600 text-white px-5 py-2 rounded-md cursor-not-allowed opacity-50"
              disabled
            >
              Submitted
            </button>
          ) : (
            <>
              {isDraft && isEditable && (
                <>
                  <button
                    className="bg-gray-500 hover:bg-gray-600 text-white px-5 py-2 rounded-md transition-colors duration-300"
                    onClick={() => {
                      if (onSaveDraft) {
                        onSaveDraft();
                      }
                    }}
                    disabled={items.length === 0 || !isDraft}
                  >
                    Save Draft
                  </button>
                  <button
                    className="bg-green-600 hover:bg-green-700 text-white px-5 py-2 rounded-md transition-colors duration-300"
                    onClick={handleFinalSubmit}
                    disabled={items.length === 0 || !isDraft}
                  >
                    Submit Invoice
                  </button>
                </>
              )}
            </>
          )}
        </div>
      </div>

      {modalOpen && (
        <Modal
          onClose={() => {
            setModalOpen(false);
            setError(null);
          }}
        >
          <div className="flex">
            {/* Left Column: Input Fields */}
            <div className="w-1/2 pr-4">
              {error && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                  {error}
                </div>
              )}
              <h2 className="text-xl font-bold mb-4">Add New Item</h2>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  handleAddNewItem();
                }}
              >
                <SearchableSelect
                  type="itemSearch"
                  options={[]}
                  allItems={allItems.map((item) => ({
                    ...item,
                    CategoryName: categories.find(
                      (cat) => cat.CategoryID === item.CategoryID
                    )?.CategoryName,
                  }))}
                  value={newItem.itemID}
                  onChange={() => {}}
                  onSelectItem={handleItemSearch}
                  placeholder="items"
                  label="Search Items"
                />

                <SearchableSelect
                  options={categories.map((cat) => ({
                    value: cat.CategoryID,
                    label: cat.CategoryName,
                  }))}
                  value={newItem.categoryID}
                  onChange={(value) => handleCategoryChange(value)}
                  placeholder="categories"
                  label="Category"
                />

                {newItem.categoryID && (
                  <SearchableSelect
                    options={filteredItems.map((item) => ({
                      value: item.ItemID,
                      label: item.ItemName,
                    }))}
                    value={newItem.itemID}
                    onChange={(value) => handleItemChange(value)}
                    placeholder="items"
                    label="Item"
                  />
                )}
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-1">
                    Quantity
                  </label>
                  <input
                    type="text"
                    className="border rounded w-full px-3 py-2"
                    value={newItem.quantity}
                    onChange={(e) => handleQuantityChange(e.target.value)}
                    placeholder="Enter quantity (up to 3 decimal places)"
                    required
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium">Stock</label>
                  <input
                    type="number"
                    className="border rounded w-full px-3 py-2"
                    value={newItem.stock}
                    onChange={(e) =>
                      handleNewItemFieldChange(
                        "stock",
                        parseFloat(e.target.value)
                      )
                    }
                    required
                  />
                </div>

                <button
                  type="submit"
                  className="bg-green-500 text-white px-4 py-2 rounded"
                >
                  Add Item
                </button>
                <button
                  type="button"
                  className="bg-gray-400 text-white px-4 py-2 rounded flex-grow"
                  onClick={() => setModalOpen(false)}
                >
                  Cancel
                </button>
              </form>
            </div>

            {/* Right Column: Promise Date Info */}
            <div className="w-1/2 pl-4 border-l">
              <h3 className="text-lg font-semibold mb-4">Delivery Details</h3>
              {newItem.categoryID ? (
                <>
                  <div className="mb-4">
                    <label className="block text-sm font-medium">
                      Normal Days
                    </label>
                    <input
                      type="text"
                      className="border rounded w-full px-3 py-2 bg-gray-100"
                      value={newItem.normalDays || "Not selected"}
                      readOnly
                    />
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium">
                      Commited Date
                    </label>
                    <input
                      type="date"
                      className="border rounded w-full px-3 py-2 bg-gray-100"
                      value={newItem.commitedDate || ""}
                      readOnly
                    />
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium">
                      Accounting Unit
                    </label>
                    <input
                      type="text"
                      className="border rounded w-full px-3 py-2 bg-gray-100"
                      value={newItem.accountingUnitName || "Not selected"}
                      readOnly
                    />
                  </div>
                </>
              ) : (
                <p className="text-gray-500">
                  Select a category to view delivery details
                </p>
              )}
              <div className="mb-4">
                <label className="block text-sm font-medium">Remarks</label>
                <textarea
                  className="border rounded w-full px-3 py-2"
                  value={newItem.remarks}
                  onChange={(e) =>
                    handleNewItemFieldChange("remarks", e.target.value)
                  }
                  placeholder="Optional remarks about the item"
                  rows="3"
                />
              </div>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default PHItemsTable;
